import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Tag, Divider } from 'antd';
import { AimOutlined, BulbOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';

const CORAL = '#D85A30';
const INK = '#2C2C2A';
const MUT = '#A08C82';

const fmt0 = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '0';
  const neg = n < 0;
  return (neg ? '-' : '') + Math.abs(n).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
const fmt2 = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '0.00';
  const neg = n < 0;
  return (neg ? '-' : '') + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
const wan = (v) => (Number(v) / 10000).toFixed(2) + '万';

// data 由 DecisionBoard 传入（实时滚动）：netProfit / netIncome / wxRevenue / refund / freight / isCurrentMonth / daysElapsed / daysInMonth / monthLabel
export default function EarnGoalModal({ open, onClose, data }) {
  const [target, setTarget] = useState(100000);

  useEffect(() => {
    if (open) {
      const saved = localStorage.getItem('earnGoalTarget');
      if (saved) setTarget(Number(saved) || 100000);
      // 优先读后端目标（与「目标跟进」看板同源）
      apiFetch('/api/earn-goal').then(r => r.json()).then(d => {
        if (d.success && d.data && d.data.target > 0) setTarget(d.data.target);
      }).catch(() => {});
    }
  }, [open]);

  const tgt = Math.max(0, Number(target) || 0);

  // 当前月净利（若非整月则按已过天数折算成整月，随系统数据滚动）
  const base = useMemo(() => {
    if (!data) return 0;
    let b = Number(data.netProfit) || 0;
    if (data.isCurrentMonth && data.daysElapsed > 5 && data.daysInMonth > 0) {
      b = b * data.daysInMonth / data.daysElapsed;
    }
    return b;
  }, [data]);

  const sales = (Number(data?.netIncome) || 0) + (Number(data?.wxRevenue) || 0);
  const netMargin = sales > 0 ? (Number(data.netProfit) / sales) * 100 : 0;

  const needMonths = (m) => (m > 0 ? tgt / m : 0);
  const cur = needMonths(base);

  const scenarios = [
    { label: '保守', mult: 0.8, note: '月净利回落 20%' },
    { label: '当前', mult: 1.0, note: '按最新月折算', hot: true },
    { label: '乐观', mult: 1.2, note: '月净利增长 20%' },
  ].map((s) => ({ ...s, mv: base * s.mult, mo: needMonths(base * s.mult) }));

  const suggestions = [
    { title: '退款减半', saving: (Number(data?.refund) || 0) * 0.5, desc: '拼多多每月退款拦截一半' },
    { title: '毛利率 +1 点', saving: sales * 0.01, desc: '提价或降采购成本，毛利率每涨 1 个点' },
    { title: '运费降 10%', saving: (Number(data?.freight) || 0) * 0.1, desc: '快递议价、包邮门槛优化' },
  ].map((s) => ({ ...s, newBase: base + s.saving, mo: needMonths(base + s.saving) }));

  const setAndSave = (v) => {
    const n = Math.max(0, Number(v) || 0);
    setTarget(n);
    localStorage.setItem('earnGoalTarget', String(n));
  };
  const card = {
    borderRadius: 12,
    border: '1px solid rgba(216,90,48,0.12)',
    background: '#FFF8F4',
    padding: '12px 14px',
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={640} destroyOnClose
      title={<span style={{ fontSize: 15, fontWeight: 600, color: INK }}><AimOutlined style={{ color: CORAL, marginRight: 8 }} />目标净利润测算</span>}>
      {/* 目标输入 */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: INK, fontWeight: 600 }}>我想赚</span>
        <input
          type="number" min="0" step="10000" value={target}
          onChange={(e) => setAndSave(e.target.value)}
          style={{ width: 160, height: 34, border: '1.5px solid #D85A30', borderRadius: 8, padding: '0 10px', fontSize: 15, fontWeight: 600, color: INK, background: '#fff' }}
        />
        <span style={{ fontSize: 13, color: INK, fontWeight: 600 }}>元净利润，需要多久？</span>
      </div>

      {/* 结论横幅 */}
      <div style={{ marginTop: 14, borderRadius: 12, background: 'linear-gradient(115deg, rgba(216,90,48,0.14), rgba(216,90,48,0.04))', padding: '18px 20px', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: MUT }}>按当前模式</span>
        <span style={{ fontSize: 40, fontWeight: 700, color: CORAL, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif', lineHeight: 1 }}>
          {base > 0 ? cur.toFixed(1) : '—'}
        </span>
        <span style={{ fontSize: 16, color: INK, fontWeight: 600 }}>个月</span>
        <span style={{ fontSize: 12, color: MUT, marginLeft: 'auto' }}>
          月净利 {wan(base)}（{data?.monthLabel || '—'} 折算，实时滚动）
        </span>
      </div>

      {/* 当月真实数据 */}
      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        {[
          { k: '月净利', v: '¥' + fmt0(base) },
          { k: '净利率', v: netMargin.toFixed(1) + '%' },
          { k: '月退款', v: '¥' + fmt0(data?.refund || 0) },
          { k: '月运费', v: '¥' + fmt0(data?.freight || 0) },
        ].map((x) => (
          <div key={x.k} style={{ ...card, flex: '1 1 130px', minWidth: 130 }}>
            <div style={{ fontSize: 12, color: MUT }}>{x.k}</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: INK, marginTop: 4, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>{x.v}</div>
          </div>
        ))}
      </div>

      {/* 三档情景 */}
      <div style={{ marginTop: 16, fontSize: 13, fontWeight: 600, color: INK }}>三档情景</div>
      <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
        {scenarios.map((s) => (
          <div key={s.label} style={{ ...card, flex: '1 1 180px', minWidth: 170, borderColor: s.hot ? '#D85A30' : 'rgba(216,90,48,0.12)', background: s.hot ? '#FFF0E9' : '#FFF8F4' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>{s.label}</span>
              {s.hot && <Tag color="#D85A30" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px' }}>当前</Tag>}
            </div>
            <div style={{ fontSize: 11, color: MUT, marginTop: 3 }}>{s.note}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.hot ? CORAL : INK, marginTop: 6, fontFamily: 'DIN Alternate, "Segoe UI", sans-serif' }}>
              {s.mo > 0 ? s.mo.toFixed(1) : '—'}<span style={{ fontSize: 12, fontWeight: 500, color: MUT }}> 个月</span>
            </div>
          </div>
        ))}
      </div>

      <Divider style={{ margin: '16px 0 10px' }} />

      {/* 优化建议 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: INK, marginBottom: 8 }}>
        <BulbOutlined style={{ color: '#BA7517' }} />优化建议（做一条，就少等一段时间）
      </div>
      {suggestions.map((s) => {
        const shorter = cur - s.mo;
        return (
          <div key={s.title} style={{ ...card, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px', minWidth: 180 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: INK }}>{s.title}</div>
              <div style={{ fontSize: 11, color: MUT, marginTop: 2 }}>{s.desc}</div>
            </div>
            <div style={{ fontSize: 12, color: MUT, textAlign: 'right' }}>
              每月多赚 <span style={{ color: CORAL, fontWeight: 700 }}>¥{fmt2(s.saving)}</span>
              <div style={{ marginTop: 2 }}>缩到 <span style={{ color: INK, fontWeight: 700 }}>{s.mo > 0 ? s.mo.toFixed(1) : '—'} 个月</span>{shorter > 0 ? <span style={{ color: '#639922' }}>（少 {shorter.toFixed(1)} 个月）</span> : null}</div>
            </div>
          </div>
        );
      })}

      <div style={{ fontSize: 11, color: MUT, marginTop: 4, lineHeight: 1.7 }}>
        口径：月净利 = 拼多多（到账）净利 + 微信（发货）净利，取自账务流水/财务健康权威数据；数字随公司看板每次刷新自动更新。目标金额默认读取「目标跟进」看板已保存的目标。
      </div>
    </Modal>
  );
}
